import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Eye } from "lucide-react";
import RichTextViewer from "../../components/common/RichTextViewer";

// ---------------------------------------------------------------------------
// Read-only sub-components (no interaction, just display)
// ---------------------------------------------------------------------------

const SectionIntro = ({ title, introText }) => (
  <div className="tw-w-full tw-max-w-2xl tw-space-y-6">
    <div className="tw-space-y-2">
      <h3 className="tw-text-xl tw-font-semibold tw-text-gray-900">{title}</h3>
      <RichTextViewer content={introText} className="tw-text-gray-600" />
    </div>
  </div>
);

const QuizPreview = ({ questions, startingNumber = 1 }) => (
  <div className="tw-w-full tw-max-w-2xl tw-space-y-12 tw-pb-8 md:tw-pb-12">
    {questions.map((question, qIndex) => (
      <div key={question.id} className="tw-space-y-6">
        <div className="tw-space-y-2">
          <div className="tw-text-xl tw-font-semibold tw-text-gray-900">
            <span>{startingNumber + qIndex}. </span>
            <RichTextViewer content={question.question_text} className="tw-inline [&_p]:tw-inline [&_p]:tw-my-0" />
          </div>
          {question.question_type === "multi_select" && (
            <p className="tw-text-sm tw-text-gray-500">Select all that apply</p>
          )}
          {question.question_type === "open_ended" && (
            <p className="tw-text-sm tw-text-gray-500">Open-ended response</p>
          )}
        </div>

        {["single_select", "multi_select", "true_false"].includes(question.question_type) && (
          <div className="tw-space-y-3">
            {(question.options || []).map((option) => {
              const isCorrect = (question.correct_answers || []).includes(option.id);
              return (
                <div
                  key={option.id}
                  className={`tw-w-full tw-rounded-xl tw-p-5 tw-text-left tw-transition-all ${
                    isCorrect
                      ? "tw-bg-green-50 tw-border tw-border-green-200"
                      : "tw-bg-gray-50"
                  }`}
                >
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <div className={`tw-h-6 tw-w-6 tw-flex-none tw-border-2 ${
                      isCorrect ? "tw-border-green-400 tw-bg-green-100" : "tw-border-gray-200"
                    } ${question.question_type === "multi_select" ? "tw-rounded-md" : "tw-rounded-full"}`} />
                    <p className="tw-text-base tw-font-medium tw-text-gray-700">{option.text}</p>
                    {isCorrect && (
                      <span className="tw-ml-auto tw-text-xs tw-font-medium tw-text-green-600">Correct</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {question.question_type === "open_ended" && (
          <div className="tw-space-y-3">
            <div className="tw-rounded-xl tw-border tw-border-dashed tw-border-gray-300 tw-bg-gray-50 tw-p-4 tw-text-sm tw-text-gray-400 tw-italic">
              User will type their answer here...
            </div>
            {question.model_answer && (
              <div>
                <p className="tw-mb-1.5 tw-text-xs tw-font-medium tw-text-green-600">Model answer</p>
                <div className="tw-rounded-xl tw-border tw-border-green-200 tw-bg-green-50 tw-p-4">
                  <RichTextViewer content={question.model_answer} className="tw-text-sm tw-text-green-800" />
                </div>
              </div>
            )}
          </div>
        )}

      </div>
    ))}
  </div>
);

const ConclusionPreview = ({ conclusionTitle, conclusionText }) => (
  <div className="tw-w-full tw-max-w-2xl tw-space-y-4 tw-text-center">
    <h2 className="tw-text-3xl tw-font-bold tw-text-gray-900">
      {conclusionTitle || "Course Completed!"}
    </h2>
    {conclusionText && (
      <RichTextViewer content={conclusionText} className="tw-text-gray-600" />
    )}
  </div>
);

// ---------------------------------------------------------------------------
// Main preview component
// ---------------------------------------------------------------------------

const CoursePreview = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);

  const buildSteps = useCallback((c) => {
    if (!c) return [];
    const result = [];

    // Course description as first step (if it has content)
    if (c.description) {
      result.push({
        type: "description",
        title: c.title || "Welcome",
        subtitle: "Course overview",
      });
    }

    let questionNum = 1;
    (c.sections || []).forEach((section) => {
      if (section.intro_text) {
        result.push({
          type: "intro",
          title: section.title,
          subtitle: "Section introduction",
          section,
        });
      }
      if (section.questions && section.questions.length > 0) {
        result.push({
          type: "quiz",
          title: `Quiz: ${section.title}`,
          subtitle: `${section.questions.length} question${section.questions.length > 1 ? "s" : ""}`,
          section,
          startingNumber: questionNum,
        });
        questionNum += section.questions.length;
      }
    });
    result.push({ type: "conclusion", title: "Conclusion", subtitle: "" });
    return result;
  }, []);

  const steps = useMemo(() => buildSteps(course), [course, buildSteps]);

  useEffect(() => {
    // Read draft from sessionStorage (set by CourseEditor before navigating here)
    const raw = sessionStorage.getItem("course_preview_draft");
    if (raw) {
      try {
        setCourse(JSON.parse(raw));
      } catch (err) {
        console.error("Failed to parse preview draft:", err);
      }
    }
    setLoading(false);
  }, [slug]);

  const handleNext = useCallback(() => {
    setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
  }, [steps]);

  const handlePrevious = useCallback(() => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "ArrowRight" || (e.key === "Enter" && !e.shiftKey && e.target.tagName !== "TEXTAREA")) {
        e.preventDefault();
        handleNext();
      } else if (e.key === "ArrowLeft" || (e.key === "Backspace" && e.target.tagName !== "INPUT" && e.target.tagName !== "TEXTAREA")) {
        e.preventDefault();
        handlePrevious();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleNext, handlePrevious]);

  if (loading) {
    return (
      <div className="tw-flex tw-min-h-screen tw-items-center tw-justify-center tw-bg-white">
        <div className="tw-w-full tw-max-w-lg tw-rounded-2xl tw-border tw-border-gray-200 tw-bg-white tw-p-8 tw-shadow-xl">
          <div className="tw-space-y-4">
            <div className="tw-mx-auto tw-h-8 tw-w-3/4 tw-animate-pulse tw-rounded tw-bg-gray-200" />
            <div className="tw-mx-auto tw-h-4 tw-w-5/6 tw-animate-pulse tw-rounded tw-bg-gray-200" />
          </div>
          <p className="tw-mt-6 tw-text-center tw-text-sm tw-text-gray-500">Loading preview...</p>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="tw-flex tw-min-h-screen tw-items-center tw-justify-center tw-bg-white">
        <div className="tw-text-center">
          <h2 className="tw-text-2xl tw-font-bold tw-text-gray-900">Course not found</h2>
          <button
            onClick={() => navigate("/courses")}
            className="tw-mt-4 tw-text-primary-main hover:tw-underline"
          >
            Back to courses
          </button>
        </div>
      </div>
    );
  }

  const currentStepData = steps[currentStep];

  return (
    <div className="tw-min-h-screen tw-bg-white tw-text-gray-900">
      {/* Preview banner */}
      <div className="tw-fixed tw-left-0 tw-right-0 tw-top-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-bg-amber-100 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-amber-800">
        <Eye className="tw-h-4 tw-w-4" />
        Preview mode — this is a read-only view of the course. Correct answers are highlighted.
      </div>

      {/* Back to editor navigation */}
      <div className="tw-fixed tw-left-4 tw-top-14 tw-z-50 md:tw-left-8">
        <button
          onClick={() => navigate(`/courses/${slug}/edit`)}
          className="tw-group tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-gray-100 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-600 tw-transition-all hover:tw-bg-gray-200 hover:tw-text-gray-900"
        >
          <ArrowLeft className="tw-h-4 tw-w-4 tw-transition-transform group-hover:-tw-translate-x-1" />
          Back to editor
        </button>
      </div>

      {/* Progress bar (below the preview banner) */}
      <div className="tw-fixed tw-left-0 tw-right-0 tw-top-[36px] tw-z-40 tw-h-1 tw-bg-gray-100">
        <motion.div
          className="tw-h-full tw-bg-primary-main"
          initial={{ width: "0%" }}
          animate={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          transition={{ duration: 0.3 }}
        />
      </div>

      {/* Main content */}
      <div className="tw-container tw-mx-auto tw-min-h-screen tw-max-w-4xl tw-px-5 tw-pt-28 tw-pb-36 md:tw-px-8 md:tw-pb-40">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="tw-flex tw-min-h-[60vh] tw-flex-col tw-items-center"
          >
            {/* Step title */}
            {currentStepData && (
              <div className="tw-mb-12 tw-text-center">
                <h1 className="tw-mb-3 tw-text-3xl tw-font-bold md:tw-text-4xl">
                  {currentStepData.title}
                </h1>
                {currentStepData.subtitle && (
                  <p className="tw-text-lg tw-text-gray-500">{currentStepData.subtitle}</p>
                )}
              </div>
            )}

            {/* Step content */}
            {currentStepData?.type === "description" && (
              <div className="tw-w-full tw-max-w-2xl">
                <RichTextViewer content={course.description} className="tw-text-gray-600" />
              </div>
            )}

            {currentStepData?.type === "intro" && (
              <SectionIntro title={currentStepData.section.title} introText={currentStepData.section.intro_text} />
            )}

            {currentStepData?.type === "quiz" && (
              <QuizPreview
                questions={currentStepData.section.questions}
                startingNumber={currentStepData.startingNumber}
              />
            )}

            {currentStepData?.type === "conclusion" && (
              <ConclusionPreview
                conclusionTitle={course.conclusion_title}
                conclusionText={course.conclusion_text}
              />
            )}
          </motion.div>
        </AnimatePresence>

        {/* Bottom navigation */}
        <div className="tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-border-t tw-border-gray-200 tw-bg-white/95 tw-backdrop-blur-sm">
          <div className="tw-container tw-mx-auto tw-flex tw-max-w-4xl tw-items-center tw-justify-between tw-gap-3 tw-px-4 tw-py-3 md:tw-px-6 md:tw-py-4">
            <button
              onClick={handlePrevious}
              className={`tw-flex tw-shrink-0 tw-items-center tw-gap-1.5 tw-rounded-full tw-px-4 tw-py-2.5 tw-text-sm tw-transition-all md:tw-gap-2 md:tw-px-6 md:tw-py-3 md:tw-text-base ${
                currentStep === 0
                  ? "tw-pointer-events-none tw-opacity-0"
                  : "tw-opacity-100 hover:tw-bg-gray-100"
              }`}
            >
              <ArrowLeft className="tw-h-4 tw-w-4 md:tw-h-5 md:tw-w-5" />
              Back
            </button>

            <div className="tw-shrink-0 tw-text-xs tw-text-gray-400 md:tw-text-sm md:tw-text-gray-500">
              {currentStep + 1} / {steps.length}
            </div>

            {currentStep === steps.length - 1 ? (
              <button
                onClick={() => navigate(`/courses/${slug}/edit`)}
                className="tw-shrink-0 tw-whitespace-nowrap tw-rounded-full tw-bg-primary-main tw-px-5 tw-py-2.5 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-primary-main/90 md:tw-px-8 md:tw-py-3 md:tw-text-base"
              >
                Back to editor
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="tw-flex tw-shrink-0 tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-rounded-full tw-bg-primary-main tw-px-5 tw-py-2.5 tw-text-sm tw-font-medium tw-text-white tw-transition-all hover:tw-bg-primary-main/90 md:tw-gap-2 md:tw-px-8 md:tw-py-3 md:tw-text-base"
              >
                Continue
                <ArrowRight className="tw-h-4 tw-w-4 md:tw-h-5 md:tw-w-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoursePreview;
